import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Card,
  Col,
  Collapse,
  Empty,
  Grid,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  theme,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import PageHeader from '../../components/common/PageHeader';
import {
  fetchApiConsultaErros,
  type ApiConsultaErroRow,
  type ApiConsultaErrosResponse,
  type StatusDashboardOverviewResponse,
  type StatusDashboardStatusSummary,
} from '../../services/statusDashboardService';
import {
  API_CONSULTA_CNJ_DESCRIPTION,
  API_CONSULTA_DAYS_OPTIONS,
  API_CONSULTA_EMPTY_HINT,
  API_CONSULTA_SUMMARY_DESCRIPTION,
  DASHBOARD_OVERVIEW_COL_SPAN,
  PAGE_SIZE,
  STATUS_DASHBOARD_CATEGORIA_DESCRIPTION,
  STATUS_DASHBOARD_TOP_ERROS_DESCRIPTION,
  TOP_ERROR_COLORS,
  type SummaryStatusKey,
} from '../Dashboard/shared/constants';
import {
  buildApiConsultaCnjSessionKey,
  buildApiConsultaSessionKey,
  formatNumber,
  getApiErrorMessage,
  isPanelOpen,
} from '../Dashboard/shared/utils';
import { getSectionPanelStyle, useDashboardRenderers } from '../Dashboard/shared/useDashboardRenderers';

const { useBreakpoint } = Grid;

const PART_RETRY_MS = 8000;

function hasSummaryData(summary: StatusDashboardStatusSummary | null | undefined): boolean {
  return (summary?.total ?? 0) > 0;
}

function hasTopErrorsData(
  topErrors: StatusDashboardOverviewResponse['topErrors'] | null | undefined,
): boolean {
  return Boolean(
    topErrors?.abrangencias?.length ||
    topErrors?.searchPlataformas?.length,
  );
}

function resolveSummaryState(
  response: ApiConsultaErrosResponse,
  previous: StatusDashboardStatusSummary | null,
): StatusDashboardStatusSummary | null {
  if (hasSummaryData(response.statusSummary)) {
    return response.statusSummary;
  }
  if (response.cacheIncomplete && previous && hasSummaryData(previous)) {
    return previous;
  }
  if (!response.cacheIncomplete && !response.cachePending) {
    return response.statusSummary;
  }
  return previous;
}

function resolveTopErrorsState(
  response: ApiConsultaErrosResponse,
  previous: StatusDashboardOverviewResponse['topErrors'] | null,
): StatusDashboardOverviewResponse['topErrors'] | null {
  if (hasTopErrorsData(response.topErrors)) {
    return response.topErrors;
  }
  if (response.cacheIncomplete && previous && hasTopErrorsData(previous)) {
    return previous;
  }
  if (!response.cacheIncomplete && !response.cachePending) {
    return response.topErrors;
  }
  return previous;
}

const DashboardApiConsultaContent: React.FC = () => {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [apiConsultaDays, setApiConsultaDays] = useState<3 | 7>(3);
  const [apiConsultaRows, setApiConsultaRows] = useState<ApiConsultaErroRow[]>([]);
  const [apiConsultaStatusSummary, setApiConsultaStatusSummary] = useState<StatusDashboardStatusSummary | null>(null);
  const [apiConsultaTopErrors, setApiConsultaTopErrors] = useState<StatusDashboardOverviewResponse['topErrors'] | null>(null);
  const [apiConsultaSummaryHoveredKey, setApiConsultaSummaryHoveredKey] = useState<SummaryStatusKey | null>(null);
  const [apiConsultaSummaryLoading, setApiConsultaSummaryLoading] = useState(false);
  const [apiConsultaTopErrorsLoading, setApiConsultaTopErrorsLoading] = useState(false);
  const [apiConsultaCnjLoading, setApiConsultaCnjLoading] = useState(false);
  const [apiConsultaSummaryLoaded, setApiConsultaSummaryLoaded] = useState(false);
  const [apiConsultaTopErrorsLoaded, setApiConsultaTopErrorsLoaded] = useState(false);
  const [apiConsultaCnjLoaded, setApiConsultaCnjLoaded] = useState(false);
  const [apiConsultaPage, setApiConsultaPage] = useState(1);
  const [cnjOpen, setCnjOpen] = useState(false);

  const [apiConsultaSummaryFromCache, setApiConsultaSummaryFromCache] = useState(false);
  const [apiConsultaSummaryCachedAt, setApiConsultaSummaryCachedAt] = useState<string | null>(null);
  const [apiConsultaTopErrorsFromCache, setApiConsultaTopErrorsFromCache] = useState(false);
  const [apiConsultaTopErrorsCachedAt, setApiConsultaTopErrorsCachedAt] = useState<string | null>(null);
  const [apiConsultaCnjFromCache, setApiConsultaCnjFromCache] = useState(false);
  const [apiConsultaCnjCachedAt, setApiConsultaCnjCachedAt] = useState<string | null>(null);
  const [apiConsultaSummaryCachePending, setApiConsultaSummaryCachePending] = useState(false);
  const [apiConsultaTopErrorsCachePending, setApiConsultaTopErrorsCachePending] = useState(false);
  const [apiConsultaCnjCachePending, setApiConsultaCnjCachePending] = useState(false);

  const apiConsultaSessionKeyRef = useRef<string | null>(null);
  const apiConsultaSummaryLoadingRef = useRef(false);
  const apiConsultaTopErrorsLoadingRef = useRef(false);
  const apiConsultaCnjLoadingRef = useRef(false);
  const apiConsultaSummaryRequestIdRef = useRef(0);
  const apiConsultaTopErrorsRequestIdRef = useRef(0);
  const apiConsultaAutoLoadedDaysRef = useRef<3 | 7 | null>(null);
  const apiConsultaCnjSessionKeyRef = useRef<string | null>(null);

  const {
    renderCollapseHeaderExtra,
    renderCollapseLabel,
    renderTopErrorCard,
    renderSummaryCard,
  } = useDashboardRenderers(isMobile);

  const loadApiConsultaSummary = useCallback(async (
    days: 3 | 7,
    options?: { forceRefresh?: boolean; isRetry?: boolean },
  ) => {
    const requestId = ++apiConsultaSummaryRequestIdRef.current;

    const sessionKey = buildApiConsultaSessionKey(days);
    apiConsultaSummaryLoadingRef.current = true;
    setApiConsultaSummaryLoading(true);
    try {
      const response = await fetchApiConsultaErros({
        days,
        part: 'summary',
        forceRefresh: options?.forceRefresh,
      });
      if (requestId !== apiConsultaSummaryRequestIdRef.current) return;

      if (!response.success) {
        throw new Error(response.message || 'Erro ao buscar resumo API Consulta');
      }

      setApiConsultaStatusSummary((previous) => resolveSummaryState(response, previous));
      setApiConsultaSummaryFromCache(response.fromCache === true);
      setApiConsultaSummaryCachedAt(response.fromCache ? response.cachedAt ?? null : null);
      setApiConsultaSummaryCachePending(response.cachePending === true);
      setApiConsultaSummaryLoaded(true);
      apiConsultaSessionKeyRef.current = sessionKey;
      setErrorMessage(null);

      const shouldRetry =
        !options?.forceRefresh &&
        (response.cacheIncomplete === true || response.cachePending === true) &&
        !hasSummaryData(response.statusSummary) &&
        !options?.isRetry;
      if (shouldRetry) {
        setTimeout(() => {
          if (requestId === apiConsultaSummaryRequestIdRef.current) {
            loadApiConsultaSummary(days, { isRetry: true });
          }
        }, PART_RETRY_MS);
      }
    } catch (e: unknown) {
      if (requestId !== apiConsultaSummaryRequestIdRef.current) return;
      const msg = getApiErrorMessage(e, 'Erro ao carregar resumo API Consulta');
      setErrorMessage(msg);
      message.error(msg);
    } finally {
      if (requestId === apiConsultaSummaryRequestIdRef.current) {
        apiConsultaSummaryLoadingRef.current = false;
        setApiConsultaSummaryLoading(false);
      }
    }
  }, []);

  const loadApiConsultaTopErrors = useCallback(async (
    days: 3 | 7,
    options?: { forceRefresh?: boolean; isRetry?: boolean },
  ) => {
    const requestId = ++apiConsultaTopErrorsRequestIdRef.current;

    apiConsultaTopErrorsLoadingRef.current = true;
    setApiConsultaTopErrorsLoading(true);
    try {
      const response = await fetchApiConsultaErros({
        days,
        part: 'topErrors',
        forceRefresh: options?.forceRefresh,
      });
      if (requestId !== apiConsultaTopErrorsRequestIdRef.current) return;

      if (!response.success) {
        throw new Error(response.message || 'Erro ao buscar erros API Consulta');
      }

      setApiConsultaTopErrors((previous) => resolveTopErrorsState(response, previous));
      setApiConsultaTopErrorsFromCache(response.fromCache === true);
      setApiConsultaTopErrorsCachedAt(response.fromCache ? response.cachedAt ?? null : null);
      setApiConsultaTopErrorsCachePending(
        response.cachePending === true ||
        (response.cacheIncomplete === true && !hasTopErrorsData(response.topErrors)),
      );
      setApiConsultaTopErrorsLoaded(true);
      setErrorMessage(null);

      const shouldRetry =
        !options?.forceRefresh &&
        (response.cacheIncomplete === true || response.cachePending === true) &&
        !hasTopErrorsData(response.topErrors) &&
        !options?.isRetry;
      if (shouldRetry) {
        setTimeout(() => {
          if (requestId === apiConsultaTopErrorsRequestIdRef.current) {
            loadApiConsultaTopErrors(days, { isRetry: true });
          }
        }, PART_RETRY_MS);
      }
    } catch (e: unknown) {
      if (requestId !== apiConsultaTopErrorsRequestIdRef.current) return;
      const msg = getApiErrorMessage(e, 'Erro ao carregar gráficos de erro API Consulta');
      setErrorMessage(msg);
      message.error(msg);
    } finally {
      if (requestId === apiConsultaTopErrorsRequestIdRef.current) {
        apiConsultaTopErrorsLoadingRef.current = false;
        setApiConsultaTopErrorsLoading(false);
      }
    }
  }, []);

  const handleApiConsultaSummaryRefresh = useCallback(() => {
    loadApiConsultaSummary(apiConsultaDays, { forceRefresh: true });
  }, [apiConsultaDays, loadApiConsultaSummary]);

  const handleApiConsultaTopErrorsRefresh = useCallback(() => {
    loadApiConsultaTopErrors(apiConsultaDays, { forceRefresh: true });
  }, [apiConsultaDays, loadApiConsultaTopErrors]);

  const loadApiConsultaCnj = useCallback(async (days: 3 | 7, options?: { forceRefresh?: boolean }) => {
    if (apiConsultaCnjLoadingRef.current) return;

    const sessionKey = buildApiConsultaCnjSessionKey(days);
    apiConsultaCnjLoadingRef.current = true;
    setApiConsultaCnjLoading(true);
    try {
      const resp = await fetchApiConsultaErros({
        days,
        part: 'cnj',
        forceRefresh: options?.forceRefresh,
      });
      if (!resp.success) throw new Error(resp.message || 'Erro ao buscar erros CNJ API Consulta');
      setApiConsultaRows(resp.data || []);
      setApiConsultaCnjFromCache(resp.fromCache === true);
      setApiConsultaCnjCachedAt(resp.fromCache ? resp.cachedAt ?? null : null);
      setApiConsultaCnjCachePending(resp.cachePending === true);
      setApiConsultaPage(1);
      setApiConsultaCnjLoaded(true);
      apiConsultaCnjSessionKeyRef.current = sessionKey;
      setErrorMessage(null);
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Erro ao carregar erros CNJ API Consulta');
      setErrorMessage(msg);
      message.error(msg);
    } finally {
      apiConsultaCnjLoadingRef.current = false;
      setApiConsultaCnjLoading(false);
    }
  }, []);

  const handleApiConsultaCnjRefresh = useCallback(() => {
    loadApiConsultaCnj(apiConsultaDays, { forceRefresh: true });
  }, [apiConsultaDays, loadApiConsultaCnj]);

  const handleApiConsultaDaysChange = (value: 3 | 7) => {
    apiConsultaSummaryRequestIdRef.current += 1;
    apiConsultaTopErrorsRequestIdRef.current += 1;
    setApiConsultaDays(value);
    apiConsultaSessionKeyRef.current = null;
    apiConsultaCnjSessionKeyRef.current = null;
    apiConsultaAutoLoadedDaysRef.current = null;
    setApiConsultaSummaryLoaded(false);
    setApiConsultaTopErrorsLoaded(false);
    setApiConsultaCnjLoaded(false);
    setApiConsultaStatusSummary(null);
    setApiConsultaTopErrors(null);
    setApiConsultaRows([]);
    setApiConsultaSummaryFromCache(false);
    setApiConsultaSummaryCachedAt(null);
    setApiConsultaTopErrorsFromCache(false);
    setApiConsultaTopErrorsCachedAt(null);
    setApiConsultaCnjFromCache(false);
    setApiConsultaCnjCachedAt(null);
    setApiConsultaSummaryCachePending(false);
    setApiConsultaTopErrorsCachePending(false);
    setApiConsultaCnjCachePending(false);
    setApiConsultaPage(1);
  };

  useEffect(() => {
    if (apiConsultaAutoLoadedDaysRef.current === apiConsultaDays) return;
    apiConsultaAutoLoadedDaysRef.current = apiConsultaDays;

    // Summary primeiro: evita duas queries pesadas simultâneas no CW ao abrir a página.
    void (async () => {
      await loadApiConsultaSummary(apiConsultaDays);
      if (apiConsultaAutoLoadedDaysRef.current === apiConsultaDays) {
        loadApiConsultaTopErrors(apiConsultaDays);
      }
    })();
  }, [apiConsultaDays, loadApiConsultaSummary, loadApiConsultaTopErrors]);

  useEffect(() => {
    if (!cnjOpen) return;
    const sessionKey = buildApiConsultaCnjSessionKey(apiConsultaDays);
    if (apiConsultaCnjSessionKeyRef.current === sessionKey) return;
    loadApiConsultaCnj(apiConsultaDays);
  }, [cnjOpen, apiConsultaDays, loadApiConsultaCnj]);

  const apiConsultaSummaryEmptyHint = !apiConsultaSummaryLoaded
    ? API_CONSULTA_EMPTY_HINT
    : apiConsultaSummaryCachePending
      ? 'Cache vazio — clique em recarregar para buscar no banco CW'
      : 'Sem dados para exibir';
  const apiConsultaDetailsEmptyHint = !apiConsultaTopErrorsLoaded
    ? API_CONSULTA_EMPTY_HINT
    : apiConsultaTopErrorsCachePending
      ? 'Cache vazio — clique em recarregar para buscar no banco CW'
      : 'Sem dados para exibir';
  const apiConsultaCnjEmptyHint = !apiConsultaCnjLoaded
    ? API_CONSULTA_EMPTY_HINT
    : apiConsultaCnjCachePending
      ? 'Cache vazio — clique em recarregar para buscar no banco CW'
      : 'Sem dados para exibir';
  const apiConsultaSummaryFromCacheTag = apiConsultaSummaryLoaded ? apiConsultaSummaryFromCache : undefined;
  const apiConsultaSummaryCachedAtTag = apiConsultaSummaryLoaded ? apiConsultaSummaryCachedAt : null;
  const apiConsultaTopErrorsFromCacheTag = apiConsultaTopErrorsLoaded ? apiConsultaTopErrorsFromCache : undefined;
  const apiConsultaTopErrorsCachedAtTag = apiConsultaTopErrorsLoaded ? apiConsultaTopErrorsCachedAt : null;

  const summaryCardLoading = apiConsultaSummaryLoading && !hasSummaryData(apiConsultaStatusSummary);
  const topErrorsCardLoading = apiConsultaTopErrorsLoading && !hasTopErrorsData(apiConsultaTopErrors);

  const apiConsultaColumns: ColumnsType<ApiConsultaErroRow> = [
    { title: 'Platform', dataIndex: 'platform', key: 'platform', ellipsis: true },
    { title: 'Search', dataIndex: 'search', key: 'search', ellipsis: true },
    { title: 'Query', dataIndex: 'query', key: 'query', ellipsis: true },
    { title: 'Param', dataIndex: 'param', key: 'param', width: 80 },
    { title: 'CNJ', dataIndex: 'value', key: 'value', ellipsis: true },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 90,
      render: (value: number) => <Tag color="error">{formatNumber(value)}</Tag>,
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <PageHeader title="Dashboard API Consulta" />

        {errorMessage && (
          <Alert
            type="error"
            showIcon
            message="Erro ao carregar dados"
            description={errorMessage}
            closable
            onClose={() => setErrorMessage(null)}
          />
        )}

        <div style={getSectionPanelStyle(token, isMobile)}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card styles={{ body: { padding: isMobile ? 12 : 16 } }}>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Select
                  value={apiConsultaDays}
                  options={[...API_CONSULTA_DAYS_OPTIONS]}
                  onChange={handleApiConsultaDaysChange}
                  style={{ width: isMobile ? '100%' : 200 }}
                />

                <Row gutter={[16, 16]}>
                  <Col {...DASHBOARD_OVERVIEW_COL_SPAN} style={{ display: 'flex' }}>
                    {renderSummaryCard(
                      apiConsultaStatusSummary,
                      summaryCardLoading,
                      apiConsultaSummaryHoveredKey,
                      setApiConsultaSummaryHoveredKey,
                      API_CONSULTA_SUMMARY_DESCRIPTION,
                      apiConsultaSummaryFromCacheTag,
                      handleApiConsultaSummaryRefresh,
                      apiConsultaSummaryEmptyHint,
                      apiConsultaSummaryCachedAtTag,
                      apiConsultaSummaryLoading,
                    )}
                  </Col>

                  <Col {...DASHBOARD_OVERVIEW_COL_SPAN} style={{ display: 'flex' }}>
                    {renderTopErrorCard(
                      'Por categoria',
                      apiConsultaTopErrors?.abrangencias,
                      TOP_ERROR_COLORS.abrangencias,
                      topErrorsCardLoading,
                      STATUS_DASHBOARD_CATEGORIA_DESCRIPTION,
                      {
                        fromCache: apiConsultaTopErrorsFromCacheTag,
                        emptyDescription: apiConsultaDetailsEmptyHint,
                        cachedAt: apiConsultaTopErrorsCachedAtTag,
                        orientation: 'vertical',
                      },
                    )}
                  </Col>

                  <Col {...DASHBOARD_OVERVIEW_COL_SPAN} style={{ display: 'flex' }}>
                    {renderTopErrorCard(
                      'Top 5 erros',
                      apiConsultaTopErrors?.searchPlataformas,
                      TOP_ERROR_COLORS.searchPlataformas,
                      topErrorsCardLoading,
                      STATUS_DASHBOARD_TOP_ERROS_DESCRIPTION,
                      {
                        fromCache: apiConsultaTopErrorsFromCacheTag,
                        emptyDescription: apiConsultaDetailsEmptyHint,
                        cachedAt: apiConsultaTopErrorsCachedAtTag,
                        onRefresh: handleApiConsultaTopErrorsRefresh,
                        refreshing: apiConsultaTopErrorsLoading,
                      },
                    )}
                  </Col>
                </Row>
              </Space>
            </Card>

            <Collapse
              activeKey={cnjOpen ? ['cnj'] : []}
              onChange={(keys) => setCnjOpen(isPanelOpen(keys, 'cnj'))}
              items={[
                {
                  key: 'cnj',
                  label: renderCollapseLabel(
                    'Erros CNJ agregados',
                    renderCollapseHeaderExtra(
                      apiConsultaCnjLoaded ? apiConsultaCnjFromCache : undefined,
                      apiConsultaCnjCachedAt,
                      apiConsultaCnjLoading,
                      apiConsultaCnjLoaded ? handleApiConsultaCnjRefresh : undefined,
                    ),
                    API_CONSULTA_CNJ_DESCRIPTION,
                  ),
                  children: (
                    <Card
                      title="Erros CNJ agregados"
                      styles={{ body: { padding: isMobile ? 12 : 16 } }}
                    >
                      {apiConsultaCnjLoading ? (
                        <div style={{ padding: 32, textAlign: 'center' }}>
                          <Spin />
                        </div>
                      ) : apiConsultaRows.length === 0 ? (
                        <Empty description={apiConsultaCnjEmptyHint} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      ) : (
                        <Table
                          rowKey={(row) => [row.platform, row.search, row.query, row.value].join('::')}
                          columns={apiConsultaColumns}
                          dataSource={apiConsultaRows}
                          scroll={{ x: 'max-content' }}
                          pagination={{
                            current: apiConsultaPage,
                            pageSize: PAGE_SIZE,
                            total: apiConsultaRows.length,
                            showSizeChanger: false,
                            onChange: setApiConsultaPage,
                            size: isMobile ? 'small' : 'default',
                          }}
                        />
                      )}
                    </Card>
                  ),
                },
              ]}
            />
          </Space>
        </div>
      </Space>
    </Card>
  );
};

const DashboardApiConsulta: React.FC = () => (
  <DashboardApiConsultaContent />
);

export default DashboardApiConsulta;
